import { Group, UnstyledButton, Text, Menu, ActionIcon, Box, Tooltip, Badge } from '@mantine/core';
import { IconDotsVertical } from '@tabler/icons-react';
import { useLocation, Link } from 'react-router-dom';
import { navigationItems } from '../../utils/navigationConfig';
import { usePlanningLimits } from '../../hooks/usePlanningLimits';

export const DesktopNav = () => {
  const location = useLocation();
  const { isStartPlanningDisabled, startPlanningTooltipText } = usePlanningLimits();

  // Responsive navigation: more items on larger screens, fewer only on small tablets
  const renderResponsiveNav = () => {
    return (
      <>
        {/* Extra large screens (1400px+): Show all 6 items */}
        <Group gap="xs" visibleFrom="xl">
          {navigationItems.map(renderNavButton)}
        </Group>

        {/* Large screens (1200px-1399px): Show 4 items with dots menu */}
        <Group gap="xs" visibleFrom="lg" hiddenFrom="xl">
          {navigationItems.slice(0, 4).map(renderNavButton)}
          {navigationItems.slice(4).length > 0 && renderMoreMenu(navigationItems.slice(4))}
        </Group>

        {/* Medium screens (992px-1199px): Show 3 items with dots menu */}
        <Group gap="xs" visibleFrom="md" hiddenFrom="lg">
          {navigationItems.slice(0, 3).map(renderNavButton)}
          {navigationItems.slice(3).length > 0 && renderMoreMenu(navigationItems.slice(3))}
        </Group>

        {/* Small tablets (below 992px): Show 2 items with dots menu */}
        <Group gap="xs" hiddenFrom="md">
          {navigationItems.slice(0, 2).map(renderNavButton)}
          {navigationItems.slice(2).length > 0 && renderMoreMenu(navigationItems.slice(2))}
        </Group>
      </>
    );
  };

  const renderNavButton = (item: typeof navigationItems[0]) => {
    const isActive = location.pathname === item.href || 
      (item.href !== '/' && location.pathname.startsWith(item.href));
    
    // Check if this is the Plan Meals item and if planning is disabled
    const isPlanMealsItem = item.href === '/planning';
    const isDisabled = isPlanMealsItem && isStartPlanningDisabled;
    
    const button = isDisabled ? (
      <UnstyledButton
        key={item.href}
        disabled={isDisabled}
        style={{
          padding: '6px 10px',
          backgroundColor: 'transparent',
          color: 'var(--mantine-color-gray-4)',
          transition: 'all 0.2s ease',
          textDecoration: 'none',
          cursor: 'not-allowed',
          opacity: 0.6,
          borderRadius: '28px',
        }}
        styles={{
          root: {
            '&:hover': {
              backgroundColor: 'transparent',
            }
          }
        }}
      >
        <Group gap="xs" align="center">
          {item.icon}
          <Text size="xs" fw={500}>
            {item.label}
          </Text>
          <Badge 
            size="xs" 
            variant="light" 
            color="orange"
            style={{ fontSize: '8px', marginLeft: '4px' }}
          >
            Limit reached
          </Badge>
        </Group>
      </UnstyledButton>
    ) : (
      <UnstyledButton
        key={item.href}
        component={Link}
        to={item.href}
        style={{
          padding: '6px 10px',
          backgroundColor: isActive ? 'var(--mantine-color-orange-0)' : 'transparent',
          color: isActive ? 'var(--mantine-color-orange-7)' : 'var(--mantine-color-gray-7)',
          transition: 'all 0.2s ease',
          textDecoration: 'none',
          borderRadius: '28px',
        }}
        data-active={isActive}
        styles={{
          root: {
            '&:hover': {
              backgroundColor: isActive
                ? 'var(--mantine-color-orange-1)'
                : 'var(--mantine-color-gray-0)',
            }
          }
        }}
      >
        <Group gap="xs" align="center">
          {item.icon}
          <Text size="xs" fw={isActive ? 600 : 500}>
            {item.label}
          </Text>
          {item.badge && (
            <Badge
              size="xs"
              variant="light"
              color={isActive ? "orange" : "violet"}
              style={{ fontSize: '8px' }}
            >
              {item.badge}
            </Badge>
          )}
        </Group>
      </UnstyledButton>
    );

    // Wrap with tooltip if disabled and tooltip text is available
    if (isDisabled && startPlanningTooltipText) {
      return (
        <Tooltip 
          key={item.href}
          label={startPlanningTooltipText} 
          multiline 
          maw={300}
          position="bottom"
          withArrow
        >
          <div>{button}</div>
        </Tooltip>
      );
    }

    return button;
  };

  const renderMoreMenu = (items: typeof navigationItems) => (
    <Menu shadow="md" width={200} position="bottom-end">
      <Menu.Target>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="lg"
          >
            <IconDotsVertical size={18} />
          </ActionIcon>
      </Menu.Target>

      <Menu.Dropdown>
        {items.map((item) => {
          const isActive = location.pathname === item.href ||
            (item.href !== '/' && location.pathname.startsWith(item.href));

          return (
            <Menu.Item
              key={item.href}
              component={Link}
              to={item.href}
              leftSection={item.icon}
              style={{
                backgroundColor: isActive ? 'var(--mantine-color-orange-0)' : 'transparent',
                color: isActive ? 'var(--mantine-color-orange-7)' : undefined,
                fontWeight: isActive ? 600 : 400
              }}
            >
              <Box>
                <Text size="sm">{item.label}</Text>
                <Text size="xs" c="dimmed">{item.description}</Text>
              </Box>
            </Menu.Item>
          );
        })}
      </Menu.Dropdown>
    </Menu>
  );

  return renderResponsiveNav();
};
